/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { Boton, accessAPI } from "../../../../shared";

/**
 * Qué puede leer el script. Va ARRIBA del editor: la lista es lo que se
 * consulta mientras se escribe, así que abajo del formulario no sirve.
 *
 * La lista la sirve la API desde el mismo archivo que arma el payload, para que
 * no se pueda anunciar un campo que no existe.
 */
export function CamposDisponibles() {
  const [campos, setCampos] = useState([]);
  const toast = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/producto/config/autocompletar/campos",
      null,
      (r) => setCampos(r.campos || []),
      () => setCampos([])
    );
  }, []);

  function copiar(texto) {
    navigator.clipboard.writeText(texto);
    toast.current?.show({
      severity: "success",
      summary: "Copiado",
      detail: `${texto} copiado al portapapeles`,
      life: 2000
    });
  }

  return (
    <div css={{ marginBottom: "1.5em" }}>
      <Toast ref={toast} position="top-center" />
      <p css={{ margin: "0 0 0.5em 0", color: "var(--grisTextos)", fontSize: "0.9em" }}>
        El script devuelve el valor del campo con <code>return</code>. Corre
        aislado: sin red, sin archivos y sin variables de entorno. Si falla, el
        campo queda vacío y el producto se crea igual.
      </p>
      <p css={{ margin: "0 0 0.3em 0", color: "var(--grisTextos)", fontSize: "0.9em" }}>
        Campos disponibles, click para copiar:
      </p>
      <div css={{ display: "flex", flexWrap: "wrap", gap: "0.4em" }}>
        {campos.map((c) => (
          <span
            key={c}
            onClick={() => copiar(c)}
            css={{
              backgroundColor: "var(--surface-100)",
              border: "1px solid var(--surface-300)",
              borderRadius: "4px",
              padding: "0.2em 0.5em",
              fontSize: "0.85em",
              fontFamily: "monospace",
              cursor: "pointer",
              "&:hover": { backgroundColor: "var(--surface-200)" }
            }}
          >
            {c}
          </span>
        ))}
      </div>
      <p css={{ margin: "0.5em 0 0 0", color: "var(--grisTextos)", fontSize: "0.85em" }}>
        <code>datos.campos</code> se lee por el código de cada campo, por ejemplo{" "}
        <code>datos.campos.peso</code>.
      </p>
    </div>
  );
}

/**
 * Prueba el script contra un producto real del mismo tipo de certificado, con
 * el mismo armado de datos que la ejecución de verdad. No guarda nada.
 *
 * A diferencia de la ejecución real —donde un script roto deja el campo vacío y
 * el producto se crea igual— acá el error se muestra: es lo que el admin vino a
 * buscar.
 *
 * Recibe el texto del editor, no lo guardado: la idea es probar antes de grabar.
 */
export default function SimuladorAutocompletar({ campoId, codigo }) {
  const [resultado, setResultado] = useState(null);
  const [probando, setProbando] = useState(false);

  // El resultado deja de valer en cuanto se toca el script.
  useEffect(() => setResultado(null), [codigo]);

  function probar() {
    setProbando(true);
    setResultado(null);
    accessAPI(
      "POST",
      `admin/producto/config/productocampospropios/${campoId}/simular`,
      { codigo: codigo ?? "" },
      (r) => {
        setResultado(r);
        setProbando(false);
      },
      (r) => {
        setResultado({ ok: false, error: r?.[0]?.msg || "No se pudo probar" });
        setProbando(false);
      }
    );
  }

  return (
    <div css={{ marginTop: "1em" }}>
      <Boton callback={probar} loader={probando} disabled={!codigo?.trim()}>
        Probar
      </Boton>

      {resultado?.ok && (
        <div css={{ marginTop: "0.75em" }}>
          <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
            Con el último producto cargado daría
          </div>
          <div css={{ fontSize: "1.3em", fontWeight: 600, wordBreak: "break-word" }}>
            {resultado.valor}
          </div>
        </div>
      )}

      {resultado && !resultado.ok && (
        <div css={{ marginTop: "0.75em" }}>
          <Message
            severity="error"
            css={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
            text={
              (resultado.error || "El script falló") +
              (resultado.linea ? ` (línea ${resultado.linea})` : "")
            }
          />
          <div css={{ marginTop: "0.5em", fontSize: "0.85em", color: "var(--grisTextos)" }}>
            Así como está, al crear un producto el campo quedaría vacío.
          </div>
        </div>
      )}
    </div>
  );
}
